import { Alert, Button } from '@territory/ui';
import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@territory/client/AuthContext';
import { viewAsInstallState } from '@territory/client/viewAs';
import { Splash } from './Splash';
import { AccessDenied } from './AccessDenied';
import { MagicRedeem } from './MagicRedeem';
import { stripMagicParam } from './wedgeMagicUrl';
import { ResetRedeem } from './ResetRedeem';
import { CodeAutoRedeem } from './CodeAutoRedeem';
import { WedgeLanding } from './WedgeLanding';
import { initTrackingTags } from './wedgeTrackingTags';
import { BRAND } from './wedgeBrand';

/**
 * Standalone entry gate for wedge.repvantage.io.
 *
 * 2026-05-31 HOTFIX · REMOVED the @repvantage.io -> app.repvantage.io bounce.
 * The CRM is archived/dead, so that redirect threw the whole team into a dead
 * app and looped them back to the "Sign back in" screen · nobody could get into
 * wedge. Everyone (team and external) now signs into wedge DIRECTLY with
 * Google. No surface bounce, no special team handling.
 *
 * 2026-06-02 · login simplification (Luke directive). The persona-routed code
 * redemption views are gone from WedgeLanding. ?code= + ?email= deep-links
 * still resolve via CodeAutoRedeem so the legacy HAYESBEER2026 / WALKIN2026 /
 * per-user code shared-URL paths keep working for back-compat. ?code= alone
 * (no email) now falls through to the dead-simple two-button landing ·
 * the user signs in with Google or magic link and the backend silently lifts
 * their tier via WEDGE_BETA_DOMINATE_DOMAINS / lookupOverrideTier. The ?yc=
 * reviewer deep link was dropped 2026-09-28 (YC archived).
 *
 * A logged-out visitor lands on WedgeLanding (Continue with Google primary,
 * "Email me a sign-in link" secondary). Deep links preserved: ?magic=, ?error=,
 * ?code= + ?email= (auto-redeem).
 */
/* 2026-09-14 (audit) · the verify notice is parsed ONCE per page load and held
   at module scope. useAuth().isLoading starts true in prod, so the gate's very
   first render is the Splash · readVerifyNotice used to run on THAT render and
   strip ?verified=1 / ?verify_error off the URL immediately. By the time the
   session resolved and the authenticated branch rendered, the params were gone,
   so the banner never painted: a rep who clicked the confirm button in their
   email saw nothing, and an expired link still failed silently, which is the
   exact bug the notice was added to kill. Parse on the first render, keep the
   result across every later render, and do the history rewrite in an effect
   (it is a side effect and never belonged in render). */
let _verifyNotice: ReactNode | undefined;
let _verifyStripped = false;

export function WedgeAuthGate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  /* ?magic=<jwt> · read ONCE per page load into state, then removed from the
     address bar (MagicRedeem strips it before its fetch; the effect below
     covers a visitor who is already signed in, who never mounts MagicRedeem).
     The token must live in state: once the URL is clean, re-reading it each
     render would swap MagicRedeem for the landing mid-redeem. The signed-in
     effect drops it so a later logout in this page load lands on the landing
     and does not replay a spent link. */
  const [magic, setMagic] = useState<string | null>(() => new URLSearchParams(window.location.search).get('magic'));
  useEffect(() => {
    if (isAuthenticated) setMagic(null);
  }, [isAuthenticated]);
  useEffect(() => {
    stripMagicParam();
  }, []);

  // Ad conversion tracking (Meta Pixel + LinkedIn Insight Tag) - only for the
  // logged-out surface below (WedgeLanding etc). Never runs once the gate
  // resolves to an authenticated rep. See wedgeTrackingTags.ts.
  useEffect(() => {
    /* Issue 2902 · never load ad pixels inside an admin view-as tab. */
    if (!isLoading && !isAuthenticated && viewAsInstallState() === 'off') initTrackingTags();
  }, [isLoading, isAuthenticated]);

  /* ?verified=1 / ?verify_error=<reason> · the emailed verify link lands here
     (GET /api/auth/wedge/verify redirects). Read before any branch so the
     params survive the loading render; rendered on BOTH resolved branches
     below, because a rep whose session expired while the link sat in their
     inbox still needs to be told the link expired. */
  if (_verifyNotice === undefined) _verifyNotice = readVerifyNotice();
  const verifyNotice = _verifyNotice;

  /* Strip the params once per page load so a reload does not re-announce.
     Must be an effect, not render work: on the loading render this ran before
     anything could show the banner. */
  useEffect(() => {
    if (_verifyStripped) return;
    _verifyStripped = true;
    stripVerifyParams();
  }, []);

  /* ?reset=<token> · set-or-reset password from an emailed link. Handled BEFORE
     the auth branches: the "add a password to an existing OAuth account" flow
     targets reps who are often still signed in, so gating this on !isAuthenticated
     would drop them into the app and skip the screen the email sent them to. */
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  if (resetToken) return <ResetRedeem token={resetToken} />;

  if (isLoading) return <Splash label="Checking your session" />;

  if (!isAuthenticated) {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    const code = params.get('code') || '';
    const emailHint = params.get('email') || '';
    const view = magic
      ? <MagicRedeem token={magic} />
      : err
        ? <AccessDenied reason={err} />
        : (code && emailHint)
          ? <CodeAutoRedeem code={code} email={emailHint} />
          : <WedgeLanding initialEmail={emailHint} />;
    return (
      <>
        {verifyNotice}
        {view}
      </>
    );
  }

  return (
    <>
      {verifyNotice}
      {children}
    </>
  );
}

/* Pure read · no history rewrite, no other side effect, so it is safe to call
   from render. The strip lives in stripVerifyParams below. */
function readVerifyNotice(): ReactNode {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const verified = params.get('verified');
  const verifyError = params.get('verify_error');
  if (!verified && !verifyError) return null;
  const ok = verified === '1';
  /* Issue 1952: confirmed from a browser other than the sign-up one, so the
     backend turned off the password and ended other sessions (pre-hijack
     guard). Amber, because the rep has something to do. */
  if (ok && params.get('pw_cleared') === '1') {
    return (
      <VerifyNotice
        ok={false}
        text={'Email confirmed in a different browser, so for your security we turned off your password and signed you out elsewhere. Sign in with Google or Apple, or tap "Forgot password?" to set a new one.'}
      />
    );
  }
  const text = ok
    ? 'Email confirmed. You are all set.'
    : verifyError === 'expired'
      ? 'That confirmation link expired. Request a new one from your account settings.'
      : 'That confirmation link is not valid. Request a new one from your account settings.';
  return <VerifyNotice ok={ok} text={text} />;
}

function stripVerifyParams(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('verified') && !params.has('verify_error')) return;
    params.delete('verified');
    params.delete('verify_error');
    params.delete('pw_cleared');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  } catch { /* noop */ }
}

/* 2026-09-14 (audit) · the banner is position:fixed over the top of the app and
   had no dismissal at all, so the "not valid, request a new one" copy sat on
   top of the rep's Today feed for the whole session. Give it a close button. */
/* 2026-10-06 (issue 1952) · the tint sat on nothing, so the logo showed
   through the text, the font fell back to serif on the landing, and the
   left:50% + translate box shrank to half the screen on phones. Opaque brand
   base under the tint, brand body font, inset to the screen edges. */
function VerifyNotice({ ok, text }: { ok: boolean; text: string }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <Alert
      variant="wedge-auth"
      authLook={ok ? 'verify-success' : 'verify-warning'}
      role="status"
      style={{ position: 'fixed', top: 12, left: 12, right: 12, margin: '0 auto', zIndex: 9999, maxWidth: 520 }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{text}</span>
      <Button appearance="wedge-notice" noticeLook="verify-dismiss"
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        style={{ flexShrink: 0 }}
      >
        ×
      </Button>
    </Alert>
  );
}
